export const SPEECH_MIME_TYPES = ['audio/mp4', 'audio/webm;codecs=opus', 'audio/webm']
export const MIN_SPEECH_LISTENING_MS = 800

export function normalizeSpeechMimeType(value) {
  const base = String(value || '').split(';')[0].trim().toLowerCase()
  return ['audio/mp4', 'audio/webm', 'audio/mpeg', 'audio/wav', 'audio/ogg'].includes(base) ? String(value).trim() : ''
}

export function preferredSpeechMimeType(MediaRecorderImpl = globalThis.MediaRecorder) {
  return SPEECH_MIME_TYPES.find((type) => MediaRecorderImpl?.isTypeSupported?.(type)) || ''
}

const captureError = (code, cause) => Object.assign(new Error(code, cause ? { cause } : undefined), { code })

export async function createSpeechCapture({
  onLevel = () => {}, onStopRequested = () => {}, maxDurationMs = 12000, silenceMs = 1800,
  minimumListeningMs = MIN_SPEECH_LISTENING_MS,
  mediaDevices = globalThis.navigator?.mediaDevices,
  MediaRecorderImpl = globalThis.MediaRecorder,
  AudioContextImpl = globalThis.AudioContext || globalThis.webkitAudioContext,
  requestFrame = globalThis.requestAnimationFrame?.bind(globalThis) || (() => 0),
  cancelFrame = globalThis.cancelAnimationFrame?.bind(globalThis) || (() => {}),
  now = () => performance.now(),
} = {}) {
  if (!mediaDevices?.getUserMedia) throw captureError('microphone_unavailable')
  if (!MediaRecorderImpl) throw captureError('media_recorder_unavailable')
  let stream
  try { stream = await mediaDevices.getUserMedia({ audio: { echoCancellation: true, noiseSuppression: true }, video: false }) }
  catch (error) { throw captureError(error?.name === 'NotAllowedError' || error?.name === 'SecurityError' ? 'microphone_permission_denied' : 'microphone_unavailable', error) }

  const selectedMimeType = preferredSpeechMimeType(MediaRecorderImpl)
  let recorder
  try { recorder = new MediaRecorderImpl(stream, selectedMimeType ? { mimeType: selectedMimeType } : undefined) }
  catch (error) { stream.getTracks().forEach((track) => track.stop()); throw captureError('recording_failed', error) }
  const actualMimeType = normalizeSpeechMimeType(recorder.mimeType || selectedMimeType)
  if (!actualMimeType) { stream.getTracks().forEach((track) => track.stop()); throw captureError('unsupported_audio') }

  const chunks = []
  const startedAt = now()
  let stopRequested = false, cancelled = false, settled = false, maxTimer, silenceTimer, audioContext, frame
  let resolveResult, rejectResult
  const result = new Promise((resolve, reject) => { resolveResult = resolve; rejectResult = reject })
  const cleanup = () => {
    clearTimeout(maxTimer); clearTimeout(silenceTimer); cancelFrame(frame)
    stream.getTracks().forEach((track) => track.stop())
    audioContext?.close?.(); audioContext = null
  }
  const finish = (error, value) => {
    if (settled) return
    settled = true; cleanup()
    if (error) rejectResult(error); else resolveResult(value)
  }
  const stop = () => {
    if (stopRequested || recorder.state !== 'recording') return
    stopRequested = true
    onStopRequested()
    try { recorder.stop() } catch (error) { finish(captureError('recording_failed', error)) }
  }
  const cancel = () => {
    cancelled = true; stopRequested = true
    try { if (recorder.state === 'recording') recorder.stop() } catch { /* cancellation still cleans up */ }
    finish(null, null)
  }
  recorder.ondataavailable = (event) => { if (event.data?.size) chunks.push(event.data) }
  recorder.onerror = (event) => finish(captureError('recording_failed', event?.error))
  recorder.onstop = () => {
    if (cancelled) return finish(null, null)
    // Safari dispatches the final dataavailable before stop; build only now so it is included.
    finish(null, new Blob(chunks, { type: actualMimeType }))
  }
  try {
    if (AudioContextImpl) {
      audioContext = new AudioContextImpl()
      const analyser = audioContext.createAnalyser(); analyser.fftSize = 256
      audioContext.createMediaStreamSource(stream).connect(analyser)
      const levels = new Uint8Array(analyser.frequencyBinCount); let heard = false
      const sample = () => {
        analyser.getByteFrequencyData(levels)
        const level = levels.reduce((a, b) => a + b, 0) / levels.length / 255
        onLevel(level)
        if (level > .055) {
          heard = true; clearTimeout(silenceTimer)
          const eligibleIn = Math.max(0, minimumListeningMs - (now() - startedAt))
          silenceTimer = setTimeout(stop, Math.max(silenceMs, eligibleIn))
        } else if (!heard) clearTimeout(silenceTimer)
        frame = requestFrame(sample)
      }
      sample()
    }
    recorder.start(250)
  } catch (error) { finish(captureError('recording_failed', error)); throw captureError('recording_failed', error) }
  maxTimer = setTimeout(stop, maxDurationMs)
  return { stream, recorder, result, stop, cancel, selectedMimeType, mimeType: actualMimeType, startedAt }
}
